import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Eye } from "@phosphor-icons/react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_META = {
    new: { label: "New", tone: "bg-slate-100 text-slate-700" },
    investigating: { label: "Investigating", tone: "bg-blue-50 text-[#2563EB]" },
    disputing: { label: "Disputing", tone: "bg-amber-50 text-amber-700" },
    negotiating: { label: "Negotiating", tone: "bg-violet-50 text-violet-700" },
    settled: { label: "Settled", tone: "bg-emerald-50 text-emerald-700" },
    closed: { label: "Closed", tone: "bg-slate-100 text-slate-500" },
};

export default function Cases() {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [confirmId, setConfirmId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        api.get("/cases").then((r) => setCases(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const handleDelete = async () => {
        if (!confirmId) return;
        setDeleting(true);
        try {
            await api.delete(`/cases/${confirmId}`);
            setCases((prev) => prev.filter((c) => c.id !== confirmId));
            toast.success("Case deleted");
            setConfirmId(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    const filtered = cases.filter((c) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return [c.title, c.creditor_name, c.case_number, c.client_name].some((v) => (v || "").toLowerCase().includes(s));
    });

    return (
        <div className="space-y-8" data-testid="cases-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="overline">Workbench</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">All cases</h1>
                </div>
                <Link to="/cases/new">
                    <Button data-testid="cases-new-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10">
                        <Plus size={16} className="mr-1" /> New case
                    </Button>
                </Link>
            </div>

            <div className="relative max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="cases-search" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by title, creditor, case number…" className="pl-9 h-10" />
            </div>

            <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Title</th>
                            <th className="text-left px-4 py-3 font-medium">Type</th>
                            <th className="text-left px-4 py-3 font-medium">Creditor</th>
                            <th className="text-left px-4 py-3 font-medium">Status</th>
                            <th className="text-left px-4 py-3 font-medium">Deadline</th>
                            <th className="text-right px-4 py-3 font-medium">Amount</th>
                            <th className="text-right px-4 py-3 font-medium w-32">Actions</th>
                        </tr>
                    </thead>
                    <tbody data-testid="cases-table-body">
                        {loading ? (
                            <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                                No cases. <Link to="/cases/new" className="text-[#2563EB] hover:underline">Create one →</Link>
                            </td></tr>
                        ) : filtered.map((c) => {
                            const s = STATUS_META[c.status] || STATUS_META.new;
                            return (
                                <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50">
                                    <td className="px-4 py-3">
                                        <Link to={`/cases/${c.id}`} data-testid={`case-row-${c.id}`} className="font-medium text-slate-900 hover:text-[#2563EB]">{c.title}</Link>
                                    </td>
                                    <td className="px-4 py-3 uppercase text-xs text-slate-500 tracking-wider">{c.case_type}</td>
                                    <td className="px-4 py-3 text-slate-600">{c.creditor_name}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.tone}`}>{s.label}</span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">{formatDateUS(c.deadline) || "—"}</td>
                                    <td className="px-4 py-3 text-right font-mono">${Number(c.amount_owed).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <Link to={`/cases/${c.id}`} data-testid={`case-open-${c.id}`}>
                                                <Button variant="ghost" size="sm" title="Open case" aria-label={`Open ${c.title}`} className="h-8 px-2 text-xs font-medium">
                                                    <Eye size={14} className="sm:mr-1" /> <span className="hidden sm:inline">Open</span>
                                                </Button>
                                            </Link>
                                            <Link to={`/cases/${c.id}/edit`} data-testid={`case-edit-${c.id}`}>
                                                <Button variant="ghost" size="sm" title="Edit" aria-label={`Edit ${c.title}`} className="h-8 w-8 p-0">
                                                    <PencilSimple size={14} />
                                                </Button>
                                            </Link>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                title="Delete"
                                                aria-label={`Delete ${c.title}`}
                                                onClick={() => setConfirmId(c.id)}
                                                data-testid={`case-delete-${c.id}`}
                                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash size={14} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
                <AlertDialogContent data-testid="delete-case-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl">Delete this case?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the case, its documents, AI outputs, contacts and coach history.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            data-testid="delete-confirm-btn"
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? "Deleting…" : "Yes, delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
